import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Document metadata per route and per language.
 *
 * Three things were wrong before this hook existed, and all three are visible to
 * a user:
 *
 *   1. **The title never changed with the language.** `index.html` ships a
 *      Spanish `<title>` and a hardcoded Spanish description; switching to
 *      Russian left the browser tab, the bookmark name and the search result in
 *      Spanish.
 *   2. **Most routes had no title at all**, so every page of the site shared one
 *      and a browser history entry was indistinguishable from the next.
 *   3. **`<html lang>` was set but the Open Graph locale was not**, so a shared
 *      link was described to a crawler in the wrong language.
 *
 * The company page owns its own metadata (it is generated from registry data),
 * so this hook deliberately does nothing on `/{country}/{identifier}`.
 */

/** Route patterns that own their own `<title>` and description. */
function isSelfDescribing(pathname: string): boolean {
  // `/br/19131243000197` — the public company page.
  return /^\/[a-z]{2}\/[^/]+$/i.test(pathname);
}

export function useDocumentMeta(): void {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const language = i18n.language;

  useEffect(() => {
    if (isSelfDescribing(pathname)) return undefined;

    const route = routeKeyFor(pathname);
    const title = t(`seo.${route}Title`, { defaultValue: '' }) || t('seo.defaultTitle');
    const description =
      t(`seo.${route}Description`, { defaultValue: '' }) || t('seo.defaultDescription');

    document.title = title;

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:locale', ogLocale(language));
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', window.location.href);

    const canonical = ensureCanonical();
    canonical.href = `${window.location.origin}${pathname}`;

    // The `hreflang` alternates are the same URL with an explicit `?locale=`,
    // which is how the server resolves the language for a crawler that does not
    // run JavaScript (section 23.8).
    for (const locale of ['en', 'es', 'pt-BR', 'ru']) {
      const link = ensureAlternate(locale);
      const url = new URL(window.location.href);
      url.searchParams.set('locale', locale);
      link.href = url.toString();
    }

    return undefined;
  }, [pathname, language, t]);
}

/** Map a path onto the `seo.<key>Title` / `seo.<key>Description` pair. */
function routeKeyFor(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'home';
  if (pathname.startsWith('/pricing')) return 'pricing';
  if (pathname.startsWith('/docs')) return 'docs';
  if (pathname.startsWith('/signin')) return 'signIn';
  if (pathname.startsWith('/signup')) return 'signUp';
  if (pathname.startsWith('/status')) return 'status';
  if (pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/opt-out')) return 'optOut';
  if (pathname.startsWith('/legal')) return 'legal';
  return 'default';
}

/** `es` → `es_ES`, `pt-BR` → `pt_BR`; the Open Graph spelling. */
function ogLocale(language: string): string {
  if (language === 'pt-BR') return 'pt_BR';
  if (language === 'es') return 'es_ES';
  if (language === 'ru') return 'ru_RU';
  return 'en_US';
}

function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  if (!content) return;
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function ensureCanonical(): HTMLLinkElement {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  return link;
}

function ensureAlternate(hreflang: string): HTMLLinkElement {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  return link;
}
